import { useEffect, useState } from "react";
import { withInteractiveReadTimeout } from "../../../lib/requestTimeout";
import { sponsorApi } from "../lib/api";
import { Step1Type } from "./wizard/Step1Type";
import { Step2Url } from "./wizard/Step2Url";
import { Step4Verify } from "./wizard/Step4Verify";
import { Step4BotStat } from "./wizard/Step4BotStat";
import { Step5Preview } from "./wizard/Step5Preview";
import { WizardStepHeader } from "./wizard/WizardUi";

export type ResourceDraft = {
  id?: number;
  name: string;
  type:
    | "channel_public"
    | "channel_private"
    | "chat_public"
    | "chat_private"
    | "bot";
  url: string;
  target_action: "subscribe" | "join_request" | "join_confirmed" | "start_bot";
  verification_mode: "verified" | "no_check";
  price_per_action: string;
  daily_limit: number | null;
  total_limit: number | null;
  even_distribution: boolean;
  priority: number;
  hold_enabled: boolean;
  tg_chat_id: number | null;
  system_bot_verified_at: string | null;
  // Поля для type=bot (Phase 1b — botstat-валидация).
  botstat_code: string | null;
  botstat_bot_username: string | null;
  botstat_verified_at: string | null;
  bot_token_configured?: boolean;
  bot_token_bot_username?: string | null;
  bot_token_verified_at?: string | null;
  bot_verification_method?: "token" | "botmembers";
  bot_launch_mode?: "start" | "unique_start";
};

type ResourceApiResult<T = unknown> = Promise<{
  data?: T | null;
  error?: unknown;
}>;

export type ResourceWizardApi = {
  getResource: (id: number, signal?: AbortSignal) => ResourceApiResult;
  createResource: (body: {
    name: string;
    type: ResourceDraft["type"];
  }) => ResourceApiResult;
  patchResource: (
    id: number,
    body: Record<string, unknown>,
  ) => ResourceApiResult;
  verifyResource: (id: number) => ResourceApiResult;
  submitResource: (id: number) => ResourceApiResult;
};

const EMPTY: ResourceDraft = {
  name: "",
  type: "channel_public",
  url: "",
  target_action: "subscribe",
  verification_mode: "verified",
  price_per_action: "",
  daily_limit: null,
  total_limit: null,
  even_distribution: false,
  priority: 0,
  hold_enabled: true,
  tg_chat_id: null,
  system_bot_verified_at: null,
  botstat_code: null,
  botstat_bot_username: null,
  botstat_verified_at: null,
  bot_token_configured: false,
  bot_token_bot_username: null,
  bot_token_verified_at: null,
};

type PricingMode = "paid" | "free";

const FREE_RESOURCE_FIELDS = {
  price_per_action: "0",
  daily_limit: null,
  total_limit: null,
  even_distribution: false,
  priority: 0,
  hold_enabled: false,
} satisfies Pick<
  ResourceDraft,
  | "price_per_action"
  | "daily_limit"
  | "total_limit"
  | "even_distribution"
  | "priority"
  | "hold_enabled"
>;

type Props = {
  mode: "create" | "edit";
  resourceId?: number;
  api?: ResourceWizardApi;
  pricingMode?: PricingMode;
  onDone: () => void;
  onCancel: () => void;
};

export function ResourceWizard({
  mode,
  resourceId,
  api,
  pricingMode = "paid",
  onDone,
  onCancel,
}: Props) {
  const resourceApi = (api ?? sponsorApi) as ResourceWizardApi;
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState<ResourceDraft>(
    normalizeDraftForPricingMode(EMPTY, pricingMode),
  );
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [loadingDraft, setLoadingDraft] = useState(mode === "edit");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadGeneration, setLoadGeneration] = useState(0);

  useEffect(() => {
    let active = true;
    if (mode === "create") {
      setStep(1);
      setDraft(normalizeDraftForPricingMode(EMPTY, pricingMode));
      setActionError(null);
      setLoadError(null);
      setLoadingDraft(false);
      return () => {
        active = false;
      };
    }
    if (mode === "edit" && !resourceId) {
      setLoadError("не удалось открыть ресурс");
      setLoadingDraft(false);
      return () => {
        active = false;
      };
    }
    if (mode === "edit" && resourceId) {
      setStep(1);
      setActionError(null);
      setLoadError(null);
      setLoadingDraft(true);
      void withInteractiveReadTimeout((signal) =>
        resourceApi.getResource(resourceId, signal),
      )
        .then((r) => {
          if (!active) return;
          if (!r.data) {
            setLoadError("не удалось открыть ресурс");
            return;
          }
          setDraft(
            normalizeDraftForPricingMode(
              normalizeDraft(r.data as unknown as ResourceDraft),
              pricingMode,
            ),
          );
        })
        .catch(() => {
          if (active) setLoadError("не удалось открыть ресурс");
        })
        .finally(() => {
          if (active) setLoadingDraft(false);
        });
    }
    return () => {
      active = false;
    };
  }, [mode, resourceId, resourceApi, pricingMode, loadGeneration]);

  async function ensureDraftId(): Promise<number> {
    if (draft.id) return draft.id;
    const r = await resourceApi.createResource({
      name: draft.name,
      type: draft.type,
    });
    if (r.error || !r.data) throw new Error("create failed");
    const newId = (r.data as { id: number }).id;
    // Функциональный setter, чтобы не потерять id при последующем setDraft в autosave
    // (иначе stale-closure overwrite приводил к каскадным DRAFT-дубликатам).
    setDraft((d) => ({ ...d, id: newId }));
    return newId;
  }

  async function autosave(patch: Partial<ResourceDraft>): Promise<void> {
    const patchForMode = normalizePatchForPricingMode(patch, pricingMode);
    const backendPatch = stripLocalFields(patchForMode);
    if (draft.id) {
      setDraft((d) =>
        normalizeDraftForPricingMode({ ...d, ...patchForMode }, pricingMode),
      );
      const r = await resourceApi.patchResource(
        draft.id,
        backendPatch as Record<string, unknown>,
      );
      if (r.error) throw new Error(formatApiError(r.error));
      return;
    }
    // Первое сохранение: type/name берём из patch (Step1 шлёт {name, type}),
    // чтобы draft сразу создавался с выбранным типом.
    const r = await resourceApi.createResource({
      name: patchForMode.name ?? draft.name,
      type: patchForMode.type ?? draft.type,
    });
    if (r.error || !r.data) throw new Error("create failed");
    const newId = (r.data as { id: number }).id;
    setDraft((d) =>
      normalizeDraftForPricingMode(
        { ...d, ...patchForMode, id: newId },
        pricingMode,
      ),
    );
    const remaining = Object.fromEntries(
      Object.entries(patchForMode).filter(
        ([k]) => k !== "name" && k !== "type",
      ),
    );
    if (Object.keys(remaining).length > 0) {
      const patchRes = await resourceApi.patchResource(
        newId,
        stripLocalFields(remaining),
      );
      if (patchRes.error) throw new Error(formatApiError(patchRes.error));
    }
  }

  async function runStep(action: () => Promise<void> | void): Promise<void> {
    if (saving) return;
    setSaving(true);
    setActionError(null);
    try {
      await action();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  if (loadingDraft) {
    return <div className="tp-screen">загрузка...</div>;
  }

  if (loadError) {
    return (
      <div className="tp-screen">
        <div className="tp-alert" role="alert">
          {loadError}
        </div>
        <button
          className="tp-button-secondary"
          onClick={() => setLoadGeneration((value) => value + 1)}
        >
          повторить
        </button>
        <button className="tp-button-primary" onClick={onCancel}>
          назад
        </button>
      </div>
    );
  }

  return (
    <div className="tp-wizard">
      <WizardStepHeader
        current={visibleStepInfo(step, draft).current}
        total={visibleStepInfo(step, draft).total}
        onBack={backForStep(step, draft, onCancel, setStep)}
      />
      <h1 className="tp-resource-page-title tp-resource-page-title-outside">
        добавление ресурсов
      </h1>
      {actionError && (
        <div className="tp-card-soft border border-red-500/40 text-sm text-red-200">
          {actionError}
        </div>
      )}

      {step === 1 && (
        <Step1Type
          draft={draft}
          onNext={(patch) =>
            runStep(async () => {
              await autosave(patch);
              setStep(2);
            })
          }
        />
      )}
      {step === 2 && (
        <Step2Url
          draft={draft}
          pricingMode={pricingMode}
          onBack={() => setStep(1)}
          onNext={(patch) =>
            runStep(async () => {
              await autosave(patch);
              const nextDraft = normalizeDraftForPricingMode(
                {
                  ...draft,
                  ...normalizePatchForPricingMode(patch, pricingMode),
                },
                pricingMode,
              );
              setStep(needsVerification(nextDraft) ? 4 : 5);
            })
          }
        />
      )}
      {step === 4 &&
        (draft.type === "bot" ? (
          <Step4BotStat
            api={resourceApi}
            draft={draft}
            onBack={() => setStep(2)}
            onNext={(patch) =>
              runStep(async () => {
                if (patch && Object.keys(patch).length > 0) {
                  await autosave(patch);
                }
                setStep(5);
              })
            }
          />
        ) : (
          <Step4Verify
            api={resourceApi}
            draft={draft}
            onBack={() => setStep(2)}
            onNext={() => setStep(5)}
          />
        ))}
      {step === 5 && (
        <Step5Preview
          draft={draft}
          showModerationHint={pricingMode !== "free"}
          onBack={() => setStep(needsVerification(draft) ? 4 : 2)}
          onSubmit={() =>
            runStep(async () => {
              const id = await ensureDraftId();
              const r = await resourceApi.submitResource(id);
              if (r.error) throw new Error(formatApiError(r.error));
              onDone();
            })
          }
        />
      )}
    </div>
  );
}

function formatApiError(error: unknown): string {
  if (!error) return "не удалось сохранить шаг";
  if (typeof error === "string") return error;
  try {
    const anyError = error as {
      error?: { message?: string; code?: string };
      detail?: string;
    };
    return (
      anyError.error?.message ??
      anyError.error?.code ??
      anyError.detail ??
      JSON.stringify(error)
    );
  } catch {
    return "не удалось сохранить шаг";
  }
}

function normalizeDraft(draft: ResourceDraft): ResourceDraft {
  if (draft.type !== "bot") return draft;
  return {
    ...draft,
    bot_verification_method:
      draft.bot_token_configured ||
      draft.bot_token_verified_at ||
      draft.verification_mode === "no_check"
        ? "token"
        : "botmembers",
  };
}

function normalizeDraftForPricingMode(
  draft: ResourceDraft,
  pricingMode: PricingMode,
): ResourceDraft {
  if (pricingMode === "paid") return draft;
  return { ...draft, ...FREE_RESOURCE_FIELDS };
}

function normalizePatchForPricingMode(
  patch: Partial<ResourceDraft>,
  pricingMode: PricingMode,
): Partial<ResourceDraft> {
  if (pricingMode === "paid") return patch;
  const {
    price_per_action: _pricePerAction,
    daily_limit: _dailyLimit,
    total_limit: _totalLimit,
    even_distribution: _evenDistribution,
    priority: _priority,
    hold_enabled: _holdEnabled,
    ...resourcePatch
  } = patch;
  return resourcePatch;
}

function stripLocalFields(
  patch: Partial<ResourceDraft>,
): Record<string, unknown> {
  const {
    bot_verification_method: _botVerificationMethod,
    botstat_verified_at: _botstatVerifiedAt,
    bot_token_configured: _botTokenConfigured,
    bot_token_bot_username: _botTokenBotUsername,
    bot_token_verified_at: _botTokenVerifiedAt,
    system_bot_verified_at: _systemBotVerifiedAt,
    tg_chat_id: _tgChatId,
    ...backendPatch
  } = patch;
  return backendPatch;
}

function needsVerification(draft: ResourceDraft): boolean {
  if (draft.verification_mode === "no_check" && draft.type !== "bot")
    return false;
  return draft.type === "bot" || draft.verification_mode === "verified";
}

function visibleStepInfo(
  step: number,
  draft: ResourceDraft,
): { current: number; total: number } {
  const visibleStep =
    step === 5 ? (needsVerification(draft) ? 4 : 3) : step === 4 ? 3 : step;
  const total = needsVerification(draft) ? 4 : 3;
  return { current: visibleStep, total };
}

function backForStep(
  step: number,
  draft: ResourceDraft,
  onCancel: () => void,
  setStep: (step: number) => void,
): () => void {
  if (step === 1) return onCancel;
  if (step === 5) return () => setStep(needsVerification(draft) ? 4 : 2);
  if (step === 4) return () => setStep(2);
  return () => setStep(1);
}
